import React, { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import {
  Plus, Trash2, X, Loader2, CalendarClock, Flag,
  Building2, GripVertical, Sparkles, Check,
} from "lucide-react";
import api, { fmtDate, formatApiErrorDetail } from "@/lib/api";
import DateInput from "@/components/DateInput";

/**
 * Back-Office task board. Kanban across all clients. Each task can be
 * firm-general or tied to a specific client. Statuses: todo → doing →
 * blocked → done. Click a task to edit; drag between columns to change
 * status (optional — we ship click-to-move for keyboard accessibility).
 */

const STATUSES = [
  { key: "todo", label: "To do", accent: "border-t-ink-lighter" },
  { key: "doing", label: "In progress", accent: "border-t-forest" },
  { key: "blocked", label: "Blocked", accent: "border-t-ochre" },
  { key: "done", label: "Done", accent: "border-t-emerald-500" },
];

const PRIORITIES = [
  { v: "low", l: "Low", color: "text-ink-lighter", bg: "bg-paper" },
  { v: "medium", l: "Medium", color: "text-forest", bg: "bg-forest/10" },
  { v: "high", l: "High", color: "text-ochre", bg: "bg-ochre/10" },
];

function priorityStyle(p) {
  return PRIORITIES.find((x) => x.v === p) || PRIORITIES[1];
}

export default function BackOffice() {
  const [tasks, setTasks] = useState([]);
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [clientFilter, setClientFilter] = useState("all");
  const [showNew, setShowNew] = useState(false);
  const [editing, setEditing] = useState(null);
  const [showSuggest, setShowSuggest] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const [t, c] = await Promise.all([
        api.get("/back-office/tasks"),
        api.get("/clients"),
      ]);
      setTasks(t.data.tasks || []);
      setClients(c.data.clients || []);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Could not load tasks");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  // Command-palette query params: ?new=1 opens task modal, ?suggest=1 opens AI suggester
  const [params, setParams] = useSearchParams();
  useEffect(() => {
    if (params.get("new") === "1") {
      setShowNew(true);
      const next = new URLSearchParams(params);
      next.delete("new");
      setParams(next, { replace: true });
    }
    if (params.get("suggest") === "1") {
      setShowSuggest(true);
      const next = new URLSearchParams(params);
      next.delete("suggest");
      setParams(next, { replace: true });
    }
  }, [params, setParams]);

  const filtered = useMemo(() => {
    if (clientFilter === "all") return tasks;
    if (clientFilter === "none") return tasks.filter((t) => !t.client_id);
    return tasks.filter((t) => t.client_id === clientFilter);
  }, [tasks, clientFilter]);

  const grouped = useMemo(() => {
    const map = { todo: [], doing: [], blocked: [], done: [] };
    filtered.forEach((t) => { (map[t.status] || map.todo).push(t); });
    // Sort each column: overdue first, then by due date asc, then by priority (high → low), then created desc
    const priOrder = { high: 0, medium: 1, low: 2 };
    Object.values(map).forEach((col) => col.sort((a, b) => {
      if (!!a.due_date !== !!b.due_date) return a.due_date ? -1 : 1;
      if (a.due_date && b.due_date && a.due_date !== b.due_date) return a.due_date < b.due_date ? -1 : 1;
      const p = (priOrder[a.priority] ?? 1) - (priOrder[b.priority] ?? 1);
      if (p !== 0) return p;
      return (b.created_at || "").localeCompare(a.created_at || "");
    }));
    return map;
  }, [filtered]);

  const openCount = filtered.filter((t) => t.status !== "done").length;
  const todayIso = new Date().toISOString().slice(0, 10);
  const overdueCount = filtered.filter((t) => t.status !== "done" && t.due_date && t.due_date < todayIso).length;
  const dueTodayCount = filtered.filter((t) => t.status !== "done" && t.due_date === todayIso).length;

  const onDropTask = async (taskId, newStatus) => {
    const t = tasks.find((x) => x.id === taskId);
    if (!t || t.status === newStatus) return;
    // Optimistic
    setTasks((prev) => prev.map((x) => x.id === taskId ? { ...x, status: newStatus } : x));
    try {
      await api.patch(`/back-office/tasks/${taskId}`, { status: newStatus });
    } catch (e) {
      setTasks((prev) => prev.map((x) => x.id === taskId ? { ...x, status: t.status } : x));
      toast.error("Could not move task");
    }
  };

  return (
    <div className="p-6 lg:p-10 max-w-7xl mx-auto space-y-6" data-testid="back-office-page">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <div className="overline text-forest">Firm</div>
          <h1 className="font-heading text-3xl sm:text-4xl font-medium tracking-tight mt-1">
            Back office
          </h1>
          <p className="text-ink-light mt-2 text-sm max-w-xl">
            Your internal task board across every client. Drag between columns to update status, or click a card to edit.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={() => setShowSuggest(true)}
            className="btn-secondary inline-flex items-center gap-2"
            data-testid="ai-suggest-btn"
          >
            <Sparkles className="w-4 h-4" strokeWidth={1.75} />
            Suggest tasks (AI)
          </button>
          <button
            type="button"
            onClick={() => setShowNew(true)}
            className="btn-forest inline-flex items-center gap-2"
            data-testid="new-task-btn"
          >
            <Plus className="w-4 h-4" strokeWidth={1.75} />
            New task
          </button>
        </div>
      </div>

      {/* Summary tiles */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <SummaryTile label="Open" value={openCount} testid="tile-open" />
        <SummaryTile label="Due today" value={dueTodayCount} testid="tile-due-today" accent="text-forest" />
        <SummaryTile label="Overdue" value={overdueCount} testid="tile-overdue" accent={overdueCount > 0 ? "text-ochre" : ""} />
        <SummaryTile label="Total" value={tasks.length} testid="tile-total" />
      </div>

      {/* Client filter */}
      <div className="flex items-center gap-2 flex-wrap">
        <span className="text-xs uppercase tracking-wider text-ink-lighter mr-1">Filter</span>
        <FilterChip active={clientFilter === "all"} onClick={() => setClientFilter("all")} testid="filter-all">
          All clients
        </FilterChip>
        <FilterChip active={clientFilter === "none"} onClick={() => setClientFilter("none")} testid="filter-none">
          Firm-general
        </FilterChip>
        {clients.map((c) => (
          <FilterChip
            key={c.id}
            active={clientFilter === c.id}
            onClick={() => setClientFilter(c.id)}
            testid={`filter-client-${c.id}`}
          >
            {c.business_name}
          </FilterChip>
        ))}
      </div>

      {loading ? (
        <div className="text-ink-lighter">Loading…</div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-4">
          {STATUSES.map((s) => (
            <Column
              key={s.key}
              status={s}
              tasks={grouped[s.key]}
              onDrop={onDropTask}
              onEdit={(t) => setEditing(t)}
            />
          ))}
        </div>
      )}

      {showNew && (
        <TaskModal
          clients={clients}
          onClose={() => setShowNew(false)}
          onSaved={() => { setShowNew(false); load(); }}
          defaultClientId={clientFilter !== "all" && clientFilter !== "none" ? clientFilter : null}
        />
      )}
      {editing && (
        <TaskModal
          clients={clients}
          existing={editing}
          onClose={() => setEditing(null)}
          onSaved={() => { setEditing(null); load(); }}
          onDeleted={() => { setEditing(null); load(); }}
        />
      )}
      {showSuggest && (
        <SuggestTasksModal
          clients={clients}
          onClose={() => setShowSuggest(false)}
          onAdded={() => { setShowSuggest(false); load(); }}
        />
      )}
    </div>
  );
}

function SummaryTile({ label, value, testid, accent = "" }) {
  return (
    <div className="card-surface p-4" data-testid={testid}>
      <div className="text-[10px] uppercase tracking-wider text-ink-lighter">{label}</div>
      <div className={`font-heading text-2xl num mt-1 ${accent}`}>{value}</div>
    </div>
  );
}

function FilterChip({ active, onClick, children, testid }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`text-xs rounded-full px-3 py-1 border transition-colors ${
        active
          ? "bg-forest text-white border-forest"
          : "bg-white text-ink-light border-border hover:border-forest/30 hover:text-forest"
      }`}
      data-testid={testid}
    >
      {children}
    </button>
  );
}

function Column({ status, tasks, onDrop, onEdit }) {
  const [isOver, setIsOver] = useState(false);
  return (
    <div
      className={`card-surface border-t-2 ${status.accent} p-3 flex flex-col min-h-[400px] transition-colors ${isOver ? "bg-forest/5" : ""}`}
      data-testid={`column-${status.key}`}
      onDragOver={(e) => { e.preventDefault(); setIsOver(true); }}
      onDragLeave={() => setIsOver(false)}
      onDrop={(e) => {
        e.preventDefault();
        setIsOver(false);
        const tid = e.dataTransfer.getData("text/task-id");
        if (tid) onDrop(tid, status.key);
      }}
    >
      <div className="flex items-center justify-between px-1 mb-3">
        <div className="text-xs font-medium uppercase tracking-wider text-ink-light">{status.label}</div>
        <div className="text-xs text-ink-lighter num" data-testid={`column-count-${status.key}`}>{tasks.length}</div>
      </div>
      <div className="space-y-2 flex-1">
        {tasks.length === 0 ? (
          <div className="text-xs text-ink-lighter text-center py-6 border border-dashed border-border rounded-lg">
            Nothing here
          </div>
        ) : (
          tasks.map((t) => <TaskCard key={t.id} task={t} onEdit={onEdit} />)
        )}
      </div>
    </div>
  );
}

function TaskCard({ task, onEdit }) {
  const pri = priorityStyle(task.priority);
  const todayIso = new Date().toISOString().slice(0, 10);
  const isOverdue = task.status !== "done" && task.due_date && task.due_date < todayIso;
  const isDueToday = task.status !== "done" && task.due_date === todayIso;

  return (
    <div
      draggable
      onDragStart={(e) => e.dataTransfer.setData("text/task-id", task.id)}
      onClick={() => onEdit(task)}
      className="group bg-white border border-border rounded-lg p-3 hover:border-forest/30 hover:shadow-sm cursor-pointer transition-shadow"
      data-testid={`task-card-${task.id}`}
    >
      <div className="flex items-start gap-2">
        <GripVertical className="w-3 h-3 text-ink-lighter mt-1 opacity-0 group-hover:opacity-100 shrink-0" />
        <div className="flex-1 min-w-0">
          <div className="text-sm font-medium leading-snug break-words">{task.title}</div>
          {task.description && (
            <div className="text-xs text-ink-lighter mt-1 line-clamp-2 whitespace-pre-wrap">{task.description}</div>
          )}
          <div className="flex items-center gap-2 mt-2 flex-wrap">
            <span className={`text-[10px] font-medium rounded-full px-2 py-0.5 ${pri.bg} ${pri.color} inline-flex items-center gap-1`}>
              <Flag className="w-2.5 h-2.5" strokeWidth={2} />
              {pri.l}
            </span>
            {task.client_business_name && (
              <span className="text-[10px] text-ink-light inline-flex items-center gap-1 max-w-[140px] truncate">
                <Building2 className="w-2.5 h-2.5" strokeWidth={1.75} />
                <span className="truncate">{task.client_business_name}</span>
              </span>
            )}
            {task.due_date && (
              <span
                className={`text-[10px] inline-flex items-center gap-1 num ${
                  isOverdue ? "text-ochre font-medium" : isDueToday ? "text-forest font-medium" : "text-ink-lighter"
                }`}
                data-testid={`task-due-${task.id}`}
              >
                <CalendarClock className="w-2.5 h-2.5" strokeWidth={2} />
                {fmtDate(task.due_date)}
                {isOverdue && " · overdue"}
                {isDueToday && " · today"}
              </span>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

// ---------------- Task modal (create / edit) ----------------

function TaskModal({ existing, clients, onClose, onSaved, onDeleted, defaultClientId }) {
  const isEdit = !!existing;
  const [form, setForm] = useState(() => ({
    title: existing?.title || "",
    description: existing?.description || "",
    client_id: existing?.client_id || defaultClientId || "",
    status: existing?.status || "todo",
    priority: existing?.priority || "medium",
    due_date: existing?.due_date || "",
  }));
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.title.trim()) {
      toast.error("Title is required");
      return;
    }
    setSaving(true);
    try {
      const payload = {
        ...form,
        client_id: form.client_id || null,
        due_date: form.due_date || null,
        description: form.description || null,
      };
      if (isEdit) {
        await api.patch(`/back-office/tasks/${existing.id}`, payload);
        toast.success("Task updated");
      } else {
        await api.post("/back-office/tasks", payload);
        toast.success("Task created");
      }
      onSaved();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Could not save");
      setSaving(false);
    }
  };

  const remove = async () => {
    if (!window.confirm(`Delete this task? "${existing.title}"`)) return;
    setDeleting(true);
    try {
      await api.delete(`/back-office/tasks/${existing.id}`);
      toast.success("Task deleted");
      onDeleted();
    } catch {
      toast.error("Could not delete");
      setDeleting(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-ink/30 backdrop-blur-sm"
      onClick={onClose}
      data-testid="task-modal"
    >
      <div
        className="bg-white rounded-2xl border border-border shadow-xl w-full max-w-lg"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="px-6 pt-6 pb-3 flex items-start justify-between border-b border-border">
          <div>
            <div className="overline mb-1">{isEdit ? "Edit task" : "New task"}</div>
            <h3 className="font-heading text-xl font-medium">
              {isEdit ? "Update task" : "Add to back office"}
            </h3>
          </div>
          <button onClick={onClose} className="p-1 text-ink-lighter hover:text-ink" data-testid="task-modal-close">
            <X className="w-4 h-4" />
          </button>
        </div>
        <form onSubmit={submit} className="p-6 space-y-4">
          <label className="block text-sm">
            <span className="font-medium">Title</span>
            <input
              required
              autoFocus
              value={form.title}
              onChange={(e) => setForm({ ...form, title: e.target.value })}
              className="w-full mt-1 bg-white border border-border rounded-lg px-3 py-2 focus:ring-2 focus:ring-forest/20 focus:border-forest outline-none"
              placeholder="Reconcile bank statement for July"
              data-testid="task-title"
            />
          </label>
          <label className="block text-sm">
            <span className="font-medium">Description <span className="text-ink-lighter font-normal">(optional)</span></span>
            <textarea
              value={form.description}
              onChange={(e) => setForm({ ...form, description: e.target.value })}
              rows={3}
              className="w-full mt-1 bg-white border border-border rounded-lg px-3 py-2 focus:ring-2 focus:ring-forest/20 focus:border-forest outline-none"
              data-testid="task-description"
            />
          </label>
          <div className="grid grid-cols-2 gap-4">
            <label className="block text-sm">
              <span className="font-medium">Client</span>
              <select
                value={form.client_id}
                onChange={(e) => setForm({ ...form, client_id: e.target.value })}
                className="w-full mt-1 bg-white border border-border rounded-lg px-3 py-2 focus:ring-2 focus:ring-forest/20 focus:border-forest outline-none"
                data-testid="task-client"
              >
                <option value="">Firm-general</option>
                {clients.map((c) => <option key={c.id} value={c.id}>{c.business_name}</option>)}
              </select>
            </label>
            <label className="block text-sm">
              <span className="font-medium">Status</span>
              <select
                value={form.status}
                onChange={(e) => setForm({ ...form, status: e.target.value })}
                className="w-full mt-1 bg-white border border-border rounded-lg px-3 py-2 focus:ring-2 focus:ring-forest/20 focus:border-forest outline-none"
                data-testid="task-status"
              >
                {STATUSES.map((s) => <option key={s.key} value={s.key}>{s.label}</option>)}
              </select>
            </label>
            <label className="block text-sm">
              <span className="font-medium">Priority</span>
              <select
                value={form.priority}
                onChange={(e) => setForm({ ...form, priority: e.target.value })}
                className="w-full mt-1 bg-white border border-border rounded-lg px-3 py-2 focus:ring-2 focus:ring-forest/20 focus:border-forest outline-none"
                data-testid="task-priority"
              >
                {PRIORITIES.map((p) => <option key={p.v} value={p.v}>{p.l}</option>)}
              </select>
            </label>
            <div className="block text-sm">
              <span className="font-medium">Due date <span className="text-ink-lighter font-normal">(optional)</span></span>
              <DateInput
                value={form.due_date}
                onChange={(iso) => setForm({ ...form, due_date: iso })}
                className="mt-1"
                data-testid="task-due-date"
              />
            </div>
          </div>
          <div className="flex items-center justify-between gap-2 pt-2 border-t border-border">
            {isEdit ? (
              <button
                type="button"
                onClick={remove}
                disabled={deleting}
                className="text-sm text-destructive inline-flex items-center gap-1.5 hover:underline"
                data-testid="task-delete"
              >
                {deleting ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Trash2 className="w-3.5 h-3.5" />}
                Delete
              </button>
            ) : <span />}
            <div className="flex gap-2">
              <button type="button" onClick={onClose} className="btn-secondary">Cancel</button>
              <button type="submit" disabled={saving} className="btn-forest inline-flex items-center gap-2" data-testid="task-save">
                {saving && <Loader2 className="w-4 h-4 animate-spin" />}
                {isEdit ? "Save" : "Create task"}
              </button>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
}

// ---------------- AI suggest modal ----------------

function SuggestTasksModal({ clients, onClose, onAdded }) {
  const [loading, setLoading] = useState(true);
  const [suggestions, setSuggestions] = useState([]);
  const [reasoning, setReasoning] = useState("");
  const [selected, setSelected] = useState(new Set());
  const [saving, setSaving] = useState(false);

  const clientNameById = useMemo(() => {
    const m = {};
    clients.forEach((c) => { m[c.id] = c.business_name; });
    return m;
  }, [clients]);

  useEffect(() => {
    let cancel = false;
    (async () => {
      try {
        const { data } = await api.post("/back-office/suggest-tasks");
        if (cancel) return;
        setSuggestions(data.suggestions || []);
        setReasoning(data.reasoning || "");
        // Pre-select all high-priority + medium
        const preset = new Set();
        (data.suggestions || []).forEach((s, i) => {
          if (s.priority !== "low") preset.add(i);
        });
        setSelected(preset);
      } catch (e) {
        if (!cancel) toast.error(formatApiErrorDetail(e.response?.data?.detail) || "AI suggestion failed");
      } finally {
        if (!cancel) setLoading(false);
      }
    })();
    return () => { cancel = true; };
  }, []);

  const toggle = (i) => {
    const next = new Set(selected);
    if (next.has(i)) next.delete(i); else next.add(i);
    setSelected(next);
  };

  const toggleAll = () => {
    if (selected.size === suggestions.length) setSelected(new Set());
    else setSelected(new Set(suggestions.map((_, i) => i)));
  };

  const addSelected = async () => {
    if (selected.size === 0) return;
    const tasks = [...selected].map((i) => suggestions[i]);
    setSaving(true);
    try {
      const { data } = await api.post("/back-office/suggest-tasks/accept", { tasks });
      toast.success(`Added ${data.created} task${data.created === 1 ? "" : "s"}`);
      onAdded();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Could not add");
      setSaving(false);
    }
  };

  const allChecked = suggestions.length > 0 && selected.size === suggestions.length;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-ink/30 backdrop-blur-sm"
      onClick={onClose}
      data-testid="suggest-tasks-modal"
    >
      <div
        className="bg-white rounded-2xl border border-border shadow-xl w-full max-w-2xl max-h-[85vh] overflow-hidden flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="px-6 pt-6 pb-3 flex items-start justify-between border-b border-border">
          <div>
            <div className="overline mb-1 inline-flex items-center gap-1.5"><Sparkles className="w-3 h-3" /> AI advisor</div>
            <h3 className="font-heading text-xl font-medium">Suggested tasks for this week</h3>
            <p className="text-sm text-ink-light mt-1">
              Claude Sonnet 4.5 read every client&apos;s status and picked out concrete work. Review, then add the ones you want.
            </p>
          </div>
          <button onClick={onClose} className="p-1 text-ink-lighter hover:text-ink" data-testid="suggest-tasks-close">
            <X className="w-4 h-4" />
          </button>
        </div>

        <div className="overflow-y-auto flex-1">
          {loading ? (
            <div className="flex flex-col items-center justify-center py-16 text-ink-lighter" data-testid="suggest-loading">
              <Loader2 className="w-6 h-6 animate-spin text-forest mb-3" />
              <div className="text-sm">Reading your firm&apos;s state…</div>
              <div className="text-xs mt-1">Usually 5-10 seconds</div>
            </div>
          ) : suggestions.length === 0 ? (
            <div className="p-8 text-center text-sm text-ink-lighter" data-testid="suggest-empty">
              <Sparkles className="w-6 h-6 text-ink-lighter mx-auto mb-2" strokeWidth={1.5} />
              <div>No new suggestions right now.</div>
              {reasoning && <div className="mt-1 max-w-sm mx-auto">{reasoning}</div>}
            </div>
          ) : (
            <>
              {reasoning && (
                <div className="px-6 pt-4 pb-2 text-xs text-ink-light italic border-b border-border">
                  &ldquo;{reasoning}&rdquo;
                </div>
              )}
              <div className="px-6 py-3 flex items-center justify-between border-b border-border">
                <button
                  type="button"
                  onClick={toggleAll}
                  className="text-xs text-forest hover:underline inline-flex items-center gap-1"
                  data-testid="suggest-toggle-all"
                >
                  {allChecked ? "Clear all" : "Select all"}
                </button>
                <div className="text-xs text-ink-lighter">
                  <span className="num font-medium text-ink">{selected.size}</span> of {suggestions.length} selected
                </div>
              </div>
              <div className="divide-y divide-border">
                {suggestions.map((s, i) => {
                  const isChecked = selected.has(i);
                  const pri = priorityStyle(s.priority);
                  return (
                    <label
                      key={i}
                      className={`flex items-start gap-3 px-6 py-3 cursor-pointer hover:bg-paper/60 ${isChecked ? "bg-forest/[0.03]" : ""}`}
                      data-testid={`suggest-item-${i}`}
                    >
                      <input
                        type="checkbox"
                        checked={isChecked}
                        onChange={() => toggle(i)}
                        className="mt-1 w-4 h-4 accent-forest shrink-0"
                        data-testid={`suggest-check-${i}`}
                      />
                      <div className="flex-1 min-w-0">
                        <div className="text-sm font-medium leading-snug">{s.title}</div>
                        {s.description && (
                          <div className="text-xs text-ink-light mt-1">{s.description}</div>
                        )}
                        <div className="flex items-center gap-2 mt-2 flex-wrap">
                          <span className={`text-[10px] font-medium rounded-full px-2 py-0.5 ${pri.bg} ${pri.color} inline-flex items-center gap-1`}>
                            <Flag className="w-2.5 h-2.5" strokeWidth={2} />
                            {pri.l}
                          </span>
                          {s.client_id && clientNameById[s.client_id] && (
                            <span className="text-[10px] text-ink-light inline-flex items-center gap-1">
                              <Building2 className="w-2.5 h-2.5" strokeWidth={1.75} />
                              {clientNameById[s.client_id]}
                            </span>
                          )}
                          {!s.client_id && (
                            <span className="text-[10px] text-ink-lighter italic">Firm-general</span>
                          )}
                          {s.due_date && (
                            <span className="text-[10px] text-ink-lighter num inline-flex items-center gap-1">
                              <CalendarClock className="w-2.5 h-2.5" strokeWidth={2} />
                              {fmtDate(s.due_date)}
                            </span>
                          )}
                        </div>
                      </div>
                    </label>
                  );
                })}
              </div>
            </>
          )}
        </div>

        {!loading && suggestions.length > 0 && (
          <div className="px-6 py-4 border-t border-border flex justify-end gap-2">
            <button onClick={onClose} className="btn-secondary">Cancel</button>
            <button
              onClick={addSelected}
              disabled={saving || selected.size === 0}
              className="btn-forest inline-flex items-center gap-2 disabled:opacity-40"
              data-testid="suggest-add-selected"
            >
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />}
              Add {selected.size} task{selected.size === 1 ? "" : "s"}
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

